// Transactional OT approve/reject — the authoritative enforcement point.
//
// Modeled on payrollCompletionService: the maker-checker policy is re-read
// inside a Firestore transaction so a stale tab or a direct console call cannot
// bypass the UI disabling in OTApprovalPage. The OT approval status lives inside
// the timeRecords[] array of a dailyTimeRecords doc, so we read the doc, replace
// the one element, and write the whole array back atomically (transactions retry
// on contention — no arrayRemove/arrayUnion race).

import { doc, runTransaction, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/config";
import { updateOtApprovalSummary } from "./otApprovalSummaryService";
import { getMaker } from "../utils/otApprovalPolicy";
import { payrollIndexRef } from "./payrollIndexService";
import { deriveLockStatus } from "./dtrLockDerivation";

// Derive the dailyTimeRecords doc id ("YYYY-MM_employeeId") from a record id
// of the form "rec_YYYYMMDD_###" (mirrors timeRecordService.updateTimeRecord).
const docIdForRecord = (recordId, employeeId) => {
  const parts = recordId.split("_");
  if (parts.length !== 3 || parts[0] !== "rec") {
    throw new Error("Invalid record ID format");
  }
  const dateStr = parts[1]; // YYYYMMDD
  const monthId = `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}`;
  return `${monthId}_${employeeId}`;
};

/**
 * Shared approve/reject transaction.
 * @param {'approved'|'rejected'} targetStatus
 */
const actionOtRecord = async ({ companyId, employeeId, recordId, approverUserId, reason = null, targetStatus }) => {
  if (targetStatus === "rejected" && (!reason || !reason.trim())) {
    throw new Error("Rejection reason is required.");
  }

  const dtrRef = doc(db, "companies", companyId, "dailyTimeRecords", docIdForRecord(recordId, employeeId));
  const settingsRef = doc(db, "companies", companyId, "settings", "attendancePolicy");
  const masterRef = payrollIndexRef(companyId);

  const result = await runTransaction(db, async (tx) => {
    const [dtrSnap, settingsSnap, masterSnap] = await Promise.all([tx.get(dtrRef), tx.get(settingsRef), tx.get(masterRef)]);
    if (!dtrSnap.exists()) throw new Error("Time record document not found.");

    const data = dtrSnap.data();
    const timeRecords = Array.isArray(data.timeRecords) ? data.timeRecords : [];
    const idx = timeRecords.findIndex((r) => r.id === recordId);
    if (idx === -1) throw new Error("Time record not found.");

    const record = timeRecords[idx];
    if (!record.otApproval) throw new Error("This record has no overtime to approve.");

    const currentStatus = record.otApproval.status;

    // Race guard — block re-actioning into the same terminal state.
    if (currentStatus === targetStatus) {
      throw new Error("This record has already been actioned.");
    }

    // Payroll lock — blocks both modes. Derived in-transaction from the index
    // (C1/C5): masterRef is read in the tx above, so this check is atomic with the
    // OT write — a run can't lock the record between the check and commit (no TOCTOU).
    const indexRuns = masterSnap.exists() ? (masterSnap.data().runs || []) : [];
    const lockStatus = deriveLockStatus(employeeId, record.date, indexRuns)?.status || null;
    if (lockStatus === "completed") {
      throw new Error("This record is locked by a completed payroll.");
    }
    if (lockStatus === "pending-approval") {
      throw new Error("This record is locked by a pending payroll approval.");
    }

    // Server-authoritative maker-checker — re-read the policy, never trust the caller.
    const policy = settingsSnap.exists()
      ? settingsSnap.data().otApprovalType || "self-approval"
      : "self-approval";
    if (policy === "maker-checker" && getMaker(record) === approverUserId) {
      throw new Error("Maker-checker is enabled. You cannot approve your own overtime.");
    }

    const newOtApproval = {
      ...record.otApproval,
      status: targetStatus,
      auditTrail: [
        ...(record.otApproval.auditTrail || []),
        {
          action: targetStatus, // 'approved' | 'rejected'
          by: approverUserId,
          at: new Date(),
          reason: targetStatus === "rejected" ? reason.trim() : null,
          otHours: record.overtimeHours ?? 0,
        },
      ],
    };

    // Replace only otApproval on the element. NB: do NOT touch updatedBy — it is
    // the basis for maker determination (who last set the OT value), not the actor.
    const newRecord = { ...record, otApproval: newOtApproval };
    const newTimeRecords = [...timeRecords];
    newTimeRecords[idx] = newRecord;

    tx.update(dtrRef, { timeRecords: newTimeRecords, updatedAt: serverTimestamp() });

    return { oldStatus: currentStatus, newStatus: targetStatus, otApproval: newOtApproval };
  });

  // Best-effort summary counter update (non-fatal — counters self-correct on
  // reload). oldStatus !== newStatus always holds here: the in-transaction race
  // guard throws when they're equal.
  updateOtApprovalSummary(companyId, result.oldStatus, result.newStatus).catch(console.error);

  return { success: true, ...result };
};

export const approveOtRecord = ({ companyId, employeeId, recordId, approverUserId }) =>
  actionOtRecord({ companyId, employeeId, recordId, approverUserId, targetStatus: "approved" });

export const rejectOtRecord = ({ companyId, employeeId, recordId, approverUserId, reason }) =>
  actionOtRecord({ companyId, employeeId, recordId, approverUserId, reason, targetStatus: "rejected" });
